import {ConfirmationDialog, date, dateTime, DetailPane, FormDialog, name} from '@management-ui/core';
import {Button, styled} from '@mui/material';
import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {usePermissions} from '../hooks';
import DateForm from '../modules/admin/forms/DateForm';
import ReferenceForm from '../modules/admin/forms/ReferenceForm';
import UserSelectionForm from '../modules/admin/forms/UserSelectionForm';
import {saveDate} from '../utilities';
import withOrderStatuses from './withOrderStatuses';

const Actions = styled('div')(({theme}) => ({
  display: 'flex',
  flexWrap: 'wrap',
  margin: theme.spacing(-1, 0, 0),

  '& .MuiButton-root': {
    color: theme.palette.common.white,
    margin: theme.spacing(0.5, 1, 0.5, 0)
  }
}));

const Attribute = styled('span')(() => ({
  '&.update': {
    cursor: 'pointer',

    '&:after': {
      content: '" - Update"',
      opacity: 0,
      transition: '0.25s opacity ease-in-out',
    },

    '&:hover:after': {
      opacity: 0.8,
    }
  }
}));

const OrderAdmin = ({type, order, onOrderUpdated, service, onLoading, statuses}) => {
  const {userCan} = usePermissions();
  const [canUnlock, setCanUnlock] = useState(false);
  const [actions, setActions] = useState([]);
  const [showLock, setShowLock] = useState(false);
  const [showUnlock, setShowUnlock] = useState(false);
  const [showDelivered, setShowDelivered] = useState(false);
  const [showWeighed, setShowWeighed] = useState(false);
  const [showChecked, setShowChecked] = useState(false);
  const [showInvoiced, setShowInvoiced] = useState(false);
  const [showHeld, setShowHeld] = useState(false);
  const [showOffHold, setShowOffHold] = useState(false);
  const [showCancelled, setShowCancelled] = useState(false);
  const [showUncancelled, setShowUncancelled] = useState(false);

  useEffect(() => {
    setCanUnlock(userCan(['unlock_orders'], true, false));
  }, [userCan]);

  const handleLock = useCallback((confirmed) => {
    setShowLock(false);
    if (confirmed) {
      onLoading(true);
      service.lockOrder(order).then((locked) => {
        onLoading(false);
        onOrderUpdated(locked);
      }).catch(() => onLoading(false));
    }
  }, [order, service, onOrderUpdated, onLoading]);

  const handleUnlock = useCallback((confirmed) => {
    setShowUnlock(false);
    if (confirmed) {
      onLoading(true);
      service.unlockOrder(order).then((locked) => {
        onLoading(false);
        onOrderUpdated(locked);
      }).catch(() => onLoading(false));
    }
  }, [order, service, onOrderUpdated, onLoading]);

  const handleDate = useCallback((field, {date}) => {
    const update = {id: order.id};
    update[field] = saveDate(date);
    return service.saveOrder(update).then((saved) => {
      onOrderUpdated(saved);
      setShowDelivered(false);
      setShowWeighed(false);
      setShowHeld(false);
      setShowCancelled(false);
    }).catch(() => null);
  }, [order, onOrderUpdated, service]);

  const handleRemoveDate = useCallback((confirmed, field) => {
    setShowOffHold(false);
    setShowUncancelled(false);
    if (confirmed) {
      handleDate(field, {date: null}).then(() => null).catch(() => null);
    }
  }, [handleDate]);

  const handleChecked = useCallback(({user_id}) => {
    return service.saveOrder({id: order.id, checked_by_id: user_id}).then((saved) => {
      onOrderUpdated(saved);
      setShowChecked(false);
    }).catch(() => null);
  }, [order, onOrderUpdated, service]);

  const handleInvoiced = useCallback(({reference}) => {
    return service.saveOrder({id: order.id, invoice_reference: reference}).then((saved) => {
      onOrderUpdated(saved);
      setShowInvoiced(false);
    }).catch(() => null);
  }, [order, onOrderUpdated, service]);

  const handleDownload = useCallback(() => {
    onLoading(true);
    service.downloadOrder(order)
      .then(() => onLoading(false))
      .catch(() => onLoading(false));
  }, [service, order, onLoading]);

  useEffect(() => {
    const getColour = (status) => statuses.find(({key}) => key === status).colour;

    const actions = [];
    if (!order.archived) {
      if (order.locked) {
        if (canUnlock) {
          actions.push({title: 'Unlock', colour: getColour('pending'), onClick: () => setShowUnlock(true)});
        }
        if (order.cancelled_at) {
          actions.push({
            title: 'Reverse Cancellation',
            colour: getColour('cancelled'),
            onClick: () => setShowUncancelled(true)
          });
        } else if (order.held_at) {
          actions.push({title: 'Take off Hold', colour: getColour('held'), onClick: () => setShowOffHold(true)});
        } else {
          if (!order.delivered_at) {
            actions.push({
              title: 'Mark as Delivered',
              colour: getColour('delivered'),
              onClick: () => setShowDelivered(true)
            });
          } else {
            if (!order.weighed_at) {
              actions.push({
                title: 'Mark as Weighed',
                colour: getColour('weighed'),
                onClick: () => setShowWeighed(true)
              });
            } else {
              if (!order.checked_by) {
                actions.push({
                  title: 'Mark as Quality Checked',
                  colour: getColour('checked'),
                  onClick: () => setShowChecked(true)
                });
              } else {
                if (!order.invoice_reference) {
                  actions.push({
                    title: 'Record Invoice Reference',
                    colour: getColour('invoiced'),
                    onClick: () => setShowInvoiced(true)
                  });
                }
              }
            }
          }
          actions.push({title: 'Put On Hold', colour: getColour('held'), onClick: () => setShowHeld(true)});
          actions.push({title: 'Cancel', colour: getColour('cancelled'), onClick: () => setShowCancelled(true)});
        }
      } else {
        actions.push(
          {title: 'Lock Order', colour: getColour('locked'), onClick: () => setShowLock(true)}
        )
      }
    }
    if (order.locked) {
      actions.push(
        {title: 'Download', colour: getColour('locked'), onClick: handleDownload}
      );
    }
    setActions(actions);
  }, [order, statuses, canUnlock, handleDownload]);

  return (
    <DetailPane
      title="Admin Details"
      details={useMemo(() => [
        {title: 'Created', value: dateTime(order.created_at)},
        {title: 'Last Updated', value: dateTime(order.updated_at)},
        ...(order.locked ? [{title: 'Locked', value: dateTime(order.locked_at)}] : []),
        ...(order.delivered_at ? [{
          title: 'Materials Delivered',
          value: (
            <Attribute {...(canUnlock ? {className: 'update', onClick: () => setShowDelivered(true)} : {})}>
              {date(order.delivered_at)}
            </Attribute>
          )
        }] : []),
        ...(order.weighed_at ? [{
          title: 'Weighed',
          value: (
            <Attribute {...(canUnlock ? {className: 'update', onClick: () => setShowWeighed(true)} : {})}>
              {date(order.weighed_at)}
            </Attribute>
          )
        }] : []),
        ...(order.checked_by ? [{
          title: 'Quality Checked',
          value: (
            <Attribute {...(canUnlock ? {className: 'update', onClick: () => setShowChecked(true)} : {})}>
              {name(order.checked_by)}
            </Attribute>
          )
        }] : []),
        ...(order.invoice_reference ? [{
          title: 'Invoice Reference',
          value: (
            <Attribute {...(canUnlock ? {className: 'update', onClick: () => setShowInvoiced(true)} : {})}>
              {order.invoice_reference}
            </Attribute>
          )
        }] : []),
        ...(order.held_at ? [{
          title: 'Put On Hold',
          value: (
            <Attribute {...(canUnlock ? {className: 'update', onClick: () => setShowHeld(true)} : {})}>
              {date(order.held_at)}
            </Attribute>
          )
        }] : []),
        ...(order.cancelled_at ? [{
          title: 'Cancelled',
          value: (
            <Attribute {...(canUnlock ? {className: 'update', onClick: () => setShowCancelled(true)} : {})}>
              {date(order.cancelled_at)}
            </Attribute>
          )
        }] : []),
        ...(order.checked_by ? [{
          title: 'Quality Checked',
          value: (
            <Attribute {...(canUnlock ? {className: 'update', onClick: () => setShowChecked(true)} : {})}>
              {name(order.checked_by)}
            </Attribute>
          )
        }] : []),
        ...(actions.length > 0 ? [{
          title: 'Actions', value: (
            <Actions>
              {actions.map(({title, colour, onClick, disabled = false}, index) => (
                <Button
                  key={index}
                  size="small"
                  variant="contained"
                  style={disabled ? {} : {backgroundColor: colour}}
                  onClick={onClick}
                  disabled={disabled}
                >
                  {title}
                </Button>
              ))}
            </Actions>
          )
        }] : [])
      ], [order, canUnlock, actions])}
      dialogs={[
        (props) => (
          <ConfirmationDialog
            {...props}
            open={showLock}
            title={`Lock ${type} Order`}
            message="Are you sure you want to lock this order?"
            onClose={handleLock}
          />
        ),
        (props) => (
          <ConfirmationDialog
            {...props}
            open={showUnlock}
            title={`Unlock ${type} Order`}
            message="Are you sure you want to unlock this order?"
            onClose={handleUnlock}
          />
        ),
        (props) => (
          <FormDialog
            {...props}
            open={showDelivered}
            title={`Mark ${type} Order as Delivered`}
            maxWidth="sm"
            onClose={() => setShowDelivered(false)}
            render={(props) => (
              <DateForm
                {...props}
                entity={{date: order.delivered_at}}
                onSave={(data) => handleDate('delivered_at', data)}/>
            )}
          />
        ),
        (props) => (
          <FormDialog
            {...props}
            open={showWeighed}
            title={`Mark ${type} Order as Weighed`}
            maxWidth="sm"
            onClose={() => setShowWeighed(false)}
            render={(props) => (
              <DateForm
                {...props}
                entity={{date: order.weighed_at}}
                onSave={(data) => handleDate('weighed_at', data)}/>
            )}
          />
        ),
        (props) => (
          <FormDialog
            {...props}
            open={showChecked}
            title={`Mark ${type} Order as Quality Checked`}
            maxWidth="sm"
            onClose={() => setShowChecked(false)}
            render={(props) => (
              <UserSelectionForm
                {...props}
                entity={{user_id: order.checked_by ? order.checked_by.id : null}}
                onSave={handleChecked}/>
            )}
          />
        ),
        (props) => (
          <FormDialog
            {...props}
            open={showInvoiced}
            title={`Mark ${type} Order as Invoiced`}
            maxWidth="sm"
            onClose={() => setShowInvoiced(false)}
            render={(props) => (
              <ReferenceForm
                {...props}
                entity={{reference: order.invoice_reference}}
                onSave={handleInvoiced}/>
            )}
          />
        ),
        (props) => (
          <FormDialog
            {...props}
            open={showHeld}
            title={`Put ${type} Order On Hold`}
            maxWidth="sm"
            onClose={() => setShowHeld(false)}
            render={(props) => (
              <DateForm
                {...props}
                entity={{date: order.held_at}}
                onSave={(data) => handleDate('held_at', data)}/>
            )}
          />
        ),
        (props) => (
          <ConfirmationDialog
            {...props}
            open={showOffHold}
            title={`Take ${type} Order off Hold`}
            message="Are you sure you want to take this order off hold?"
            onClose={(confirmed) => handleRemoveDate(confirmed, 'held_at')}
          />
        ),
        (props) => (
          <FormDialog
            {...props}
            open={showCancelled}
            title={`Mark ${type} Order as Cancelled`}
            maxWidth="sm"
            onClose={() => setShowCancelled(false)}
            render={(props) => (
              <DateForm
                {...props}
                entity={{date: order.cancelled_at}}
                onSave={(data) => handleDate('cancelled_at', data)}/>
            )}
          />
        ),
        (props) => (
          <ConfirmationDialog
            {...props}
            open={showUncancelled}
            title={`Reverse ${type} Order Cancellation?`}
            message="Are you sure you want to reverse the cancellation this order?"
            onClose={(confirmed) => handleRemoveDate(confirmed, 'cancelled_at')}
          />
        ),
      ]}
    />
  );
};

export default withOrderStatuses(OrderAdmin);
